import { css, type SerializedStyles } from '@emotion/core'
import { colors } from '@fs/zion-ui'

/**
 * Shared markdown styles for rendering markdown content across the application.
 * Uses Zion UI design system colors for consistency.
 *
 * @param className - The CSS class name to scope the styles to
 * @returns Emotion CSS styles
 */
export const createMarkdownStyles = (className = 'markdown-content'): SerializedStyles => css`
  .${className} {
    font-family: 'HeritageBody:Sans', 'HanaMinBFont', ui-sans-serif, system-ui, sans-serif, 'Tofu';
    font-size: 14px;
    line-height: 1.6;
  }

  .${className} h1 {
    font-size: 2rem;
    font-weight: 600;
    margin-bottom: 1rem;
    margin-top: 1.5rem;
    padding-bottom: 0.3em;
    color: ${colors.gray90};
    border-bottom: 1px solid ${colors.gray10};
  }

  .${className} h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    margin-top: 1.25rem;
    padding-bottom: 0.3em;
    color: ${colors.gray80};
    border-bottom: 1px solid ${colors.gray10};
  }

  .${className} h3 {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    margin-top: 1rem;
    color: ${colors.gray70};
  }

  .${className} h4 {
    font-size: 1em;
    font-weight: 600;
    margin: 1.33em 0;
    color: ${colors.gray70};
  }

  .${className} h5 {
    font-size: 0.875em;
    font-weight: 600;
    margin: 1.67em 0;
    color: ${colors.gray70};
  }

  .${className} h6 {
    font-size: 0.85em;
    font-weight: 600;
    margin: 2.33em 0;
    color: ${colors.gray60};
  }

  .${className} p {
    margin-bottom: 1rem;
    color: ${colors.gray90};
  }

  .${className} ul,
  .${className} ol {
    margin-bottom: 1rem;
    margin-inline-start: 1.5rem;
    padding-inline-start: 0.5rem;
  }

  .${className} li {
    margin-bottom: 0.5rem;
  }

  .${className} hr {
    border: none;
    border-top: 1px solid ${colors.gray10};
    margin: 1.5rem 0;
  }

  .${className} code {
    background: ${colors.gray05};
    padding: 0.2rem 0.4rem;
    border-radius: 3px;
    font-family: monospace;
    font-size: 0.9em;
  }

  .${className} pre {
    background: ${colors.gray05};
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
    margin-bottom: 1rem;
  }

  .${className} pre code {
    background: none;
    padding: 0;
  }

  .${className} blockquote {
    border-inline-start: 4px solid ${colors.blue40};
    padding-inline-start: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: ${colors.gray70};
  }

  .${className} a {
    color: ${colors.blue60};
    text-decoration: none;
  }

  .${className} a:hover {
    text-decoration: underline;
  }

  .${className} table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;
    border-spacing: 0;
    display: block;
    overflow: auto;
  }

  .${className} th,
  .${className} td {
    border: 1px solid ${colors.gray10};
    padding: 0.5rem;
    text-align: start;
    vertical-align: top;
  }

  .${className} td[align='center'] {
    text-align: center;
  }

  .${className} th {
    background: ${colors.gray05};
    font-weight: 600;
  }

  .${className} img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  .${className} img.img-sm {
    max-width: 60% !important;
  }

  .${className} img.img-xs {
    max-width: 40% !important;
  }

  .${className} img.img-border {
    border: 1px solid ${colors.gray20};
    border-radius: 4px;
  }
`
